/**
 * @file apps/web/src/lib/portalAuth.tsx
 * @description Auth context for the client portal.
 * Client users access a read-only view of their project data.
 * Sends JWT as Authorization: Bearer <token> for all API calls.
 *
 * Token refresh: same mechanism as lib/auth.tsx's AuthProvider — real
 * (non-dev) portal sessions come from supabase.auth.signInWithPassword()
 * in PortalLoginPage.tsx, so this subscribes to
 * supabase.auth.onAuthStateChange() to keep the stored access token fresh
 * as Supabase rotates it (tokens expire hourly). Dev quick-login never
 * creates a Supabase session, so this is a no-op for dev portal users.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from './supabase.js';

export interface PortalUser {
  userId:     string;
  tenantId:   string;
  email:      string;
  fullName:   string;
  projectId:  string;
  role:       string;
  clientName: string;
  token:      string; // JWT for API auth
}

// Dev seed portal users — match seed.ts IDs
export const DEV_PORTAL_USERS: Omit<PortalUser, 'token'>[] = [
  {
    userId:     '22222222-0000-4000-a000-000000000005',
    tenantId:   '11111111-0000-4000-a000-000000000001',
    email:      'jthornton@acme.com',
    fullName:   'James Thornton',
    projectId:  '44444444-0000-4000-a000-000000000001',
    role:       'client_admin',
    clientName: 'Acme Corp',
  },
  {
    userId:     '22222222-0000-4000-a000-000000000007',
    tenantId:   '11111111-0000-4000-a000-000000000001',
    email:      'lchen@acme.com',
    fullName:   'Linda Chen',
    projectId:  '44444444-0000-4000-a000-000000000001',
    role:       'client_admin',
    clientName: 'Acme Corp',
  },
];

// Keep named export for backward compat
export const DEV_PORTAL_USER = DEV_PORTAL_USERS[0]!;

const PORTAL_STORAGE_KEY = 'veridox_portal_auth';

interface PortalAuthContextValue {
  user:        PortalUser | null;
  setUser:     (u: PortalUser | null) => void;
  devHeaders:  Record<string, string>;
  isLoggedIn:  boolean;
}

export const PortalAuthContext = createContext<PortalAuthContextValue>({
  user:       null,
  setUser:    () => undefined,
  devHeaders: {},
  isLoggedIn: false,
});

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<PortalUser | null>(() => {
    try {
      const stored = localStorage.getItem(PORTAL_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as PortalUser) : null;
    } catch {
      return null;
    }
  });

  const setUser = (u: PortalUser | null) => {
    if (u) {
      localStorage.setItem(PORTAL_STORAGE_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(PORTAL_STORAGE_KEY);
    }
    setUserState(u);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.access_token) return;
      setUserState((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, token: session.access_token };
        localStorage.setItem(PORTAL_STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <PortalAuthContext.Provider
      value={{
        user,
        setUser,
        devHeaders: user?.token
          ? { 'Authorization': `Bearer ${user.token}` }
          : {},
        isLoggedIn: user !== null,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  return useContext(PortalAuthContext);
}
